import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiClock,
  FiDatabase,
  FiDollarSign,
  FiPercent,
  FiPlus,
  FiRefreshCw,
  FiShield,
  FiTarget,
  FiTrendingUp,
  FiX,
  FiZap,
} from 'react-icons/fi';
import { useAuth } from '../../auth/AuthProvider';
import {
  CoreRevenueCounterpartySummary,
  CoreRevenueIntelligence,
  CoreRevenueLedgerEvent,
  CoreRevenueMeta,
  CoreRevenuePolicy,
  CoreRevenuePolicyInput,
  CoreRevenueSettlementPreview,
  CoreRevenueSummary,
  CoreRevenueTransaction,
  createCoreRevenuePolicy,
  getCoreRevenueIntelligence,
  getCoreRevenueMeta,
  getCoreRevenueSettlementPreview,
  getCoreRevenueSummary,
  listCoreRevenueLedgerEvents,
  listCoreRevenuePolicies,
  listCoreRevenueTransactions,
  syncCoreRevenue,
  syncCoreRevenueAccountingPayments,
  updateCoreRevenuePolicy,
} from '../../api/revenue';
import './RevenuePage.css';

const DEFAULT_META: CoreRevenueMeta = {
  sourceTypes: ['supplier_bill', 'promostore_order'],
  policySourceTypes: ['all', 'supplier_bill', 'promostore_order'],
  policyStatuses: ['draft', 'active', 'paused', 'ended'],
  counterpartyTypes: ['supplier', 'decorator', 'shipping-account', 'other-service', 'promostore'],
  verificationStatuses: ['unverified', 'partial', 'verified', 'reversed'],
  eligibilityStatuses: ['observed', 'pending_verification', 'eligible', 'excluded'],
  revenueStatuses: ['observed', 'accrued', 'collected', 'reversed'],
  observationMode: true,
  collectionEnabled: false,
};

const EMPTY_POLICY: CoreRevenuePolicyInput = {
  name: '',
  status: 'draft',
  mode: 'contracted',
  sourceType: 'all',
  counterpartyType: null,
  counterpartyId: null,
  ratePercent: 2,
  includeTax: false,
  includeShipping: false,
  effectiveFrom: null,
  effectiveTo: null,
  notes: '',
};

const titleCase = (value?: string | null) =>
  String(value || '')
    .replace(/_/g, '-')
    .split('-')
    .map((part) => part ? part[0].toUpperCase() + part.slice(1) : '')
    .join(' ');

const formatAmount = (value: number, currency?: string | null) => {
  const normalized = String(currency || '').toUpperCase();
  if (/^[A-Z]{3}$/.test(normalized) && normalized !== 'UNSPECIFIED') {
    try {
      return new Intl.NumberFormat('en-CA', {
        style: 'currency',
        currency: normalized,
        maximumFractionDigits: 0,
      }).format(Number(value || 0));
    } catch {
      // fall through
    }
  }
  return `${Number(value || 0).toLocaleString('en-CA', { maximumFractionDigits: 0 })}${normalized ? ` ${normalized}` : ''}`;
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const policyStatusOptions = (
  status: CoreRevenuePolicy['status']
): CoreRevenuePolicy['status'][] => {
  if (status === 'draft') return ['draft', 'active'];
  if (status === 'active') return ['active', 'paused', 'ended'];
  if (status === 'paused') return ['paused', 'ended'];
  return ['ended'];
};

const RevenuePage: React.FC = () => {
  const { user } = useAuth();
  const isGlobal = user?.commandCentreAccess?.scope === 'global';

  const [meta, setMeta] = useState<CoreRevenueMeta>(DEFAULT_META);
  const [summary, setSummary] = useState<CoreRevenueSummary | null>(null);
  const [intelligence, setIntelligence] = useState<CoreRevenueIntelligence | null>(null);
  const [settlement, setSettlement] = useState<CoreRevenueSettlementPreview | null>(null);
  const [transactions, setTransactions] = useState<CoreRevenueTransaction[]>([]);
  const [transactionTotal, setTransactionTotal] = useState(0);
  const [ledgerEvents, setLedgerEvents] = useState<CoreRevenueLedgerEvent[]>([]);
  const [policies, setPolicies] = useState<CoreRevenuePolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [paymentSyncing, setPaymentSyncing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [policyDraft, setPolicyDraft] = useState<CoreRevenuePolicyInput>({ ...EMPTY_POLICY });
  const [savingPolicy, setSavingPolicy] = useState(false);

  const [sourceFilter, setSourceFilter] = useState('');
  const [verificationFilter, setVerificationFilter] = useState('');
  const [counterpartyFilter, setCounterpartyFilter] = useState('');

  const loadRevenue = async () => {
    if (!isGlobal) return;
    setLoading(true);
    try {
      const [metadata, summaryData, intelData, settlementData, policyRows, txResult, eventResult] = await Promise.all([
        getCoreRevenueMeta(),
        getCoreRevenueSummary(),
        getCoreRevenueIntelligence(),
        getCoreRevenueSettlementPreview(),
        listCoreRevenuePolicies(),
        listCoreRevenueTransactions({
          sourceType: sourceFilter as any,
          verificationStatus: verificationFilter || undefined,
          counterpartyType: counterpartyFilter as any,
          limit: 150,
        }),
        listCoreRevenueLedgerEvents({ limit: 20 }),
      ]);
      setMeta({ ...DEFAULT_META, ...metadata });
      setSummary(summaryData);
      setIntelligence(intelData);
      setSettlement(settlementData);
      setPolicies(policyRows);
      setTransactions(txResult.rows);
      setTransactionTotal(txResult.total);
      setLedgerEvents(eventResult.rows);
    } catch (error: any) {
      setNotice(error?.message || 'Unable to load Core revenue data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isGlobal) {
      setLoading(false);
      return;
    }
    loadRevenue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGlobal, sourceFilter, verificationFilter, counterpartyFilter]);

  const currencyBuckets = useMemo(
    () => Object.values(summary?.currencyBuckets || {}).sort((a, b) => b.observedEligible - a.observedEligible),
    [summary]
  );

  const handleSync = async (full = false) => {
    if (syncing) return;
    setSyncing(true);
    setNotice(null);
    try {
      const result = await syncCoreRevenue(full);
      setNotice(
        `Revenue ledger refreshed: ${result.totalScanned} source records scanned, ${result.totalCreated} added, ${result.totalUpdated} updated.`
      );
      await loadRevenue();
    } catch (error: any) {
      setNotice(error?.message || 'Unable to refresh the Core revenue ledger.');
    } finally {
      setSyncing(false);
    }
  };

  const handlePaymentEvidenceSync = async () => {
    if (paymentSyncing) return;
    setPaymentSyncing(true);
    setNotice(null);
    try {
      const result = await syncCoreRevenueAccountingPayments(50);
      setNotice(
        `Supplier payment evidence refreshed across ${result.payments.instanceCount} instance(s): ${result.payments.synced} synced, ${result.payments.needsReview} need review, ${result.payments.failed} failed.`
      );
      await loadRevenue();
    } catch (error: any) {
      setNotice(error?.message || 'Unable to refresh supplier payment evidence.');
    } finally {
      setPaymentSyncing(false);
    }
  };

  const openPolicyForCounterparty = (counterparty: CoreRevenueCounterpartySummary) => {
    const sourceType = counterparty.counterpartyType === 'promostore'
      ? 'promostore_order'
      : 'supplier_bill';
    setPolicyDraft({
      ...EMPTY_POLICY,
      name: `${counterparty.counterpartyName} Core revenue agreement`,
      sourceType,
      counterpartyType: counterparty.counterpartyType,
      counterpartyId: counterparty.counterpartyId || null,
      ratePercent: 2,
      status: 'draft',
    });
    setShowPolicyModal(true);
  };

  const handleCreatePolicy = async (event: FormEvent) => {
    event.preventDefault();
    if (!policyDraft.name?.trim() || savingPolicy) return;
    setSavingPolicy(true);
    setNotice(null);
    try {
      await createCoreRevenuePolicy(policyDraft);
      setShowPolicyModal(false);
      setPolicyDraft({ ...EMPTY_POLICY });
      await loadRevenue();
    } catch (error: any) {
      setNotice(error?.message || 'Unable to create Core revenue policy.');
    } finally {
      setSavingPolicy(false);
    }
  };

  const handlePolicyStatus = async (policy: CoreRevenuePolicy, status: CoreRevenuePolicy['status']) => {
    try {
      await updateCoreRevenuePolicy(policy.id, { status });
      await loadRevenue();
    } catch (error: any) {
      setNotice(error?.message || 'Unable to update Core revenue policy.');
    }
  };

  if (!isGlobal) {
    return (
      <div className="core-revenue-page">
        <section className="core-revenue-access-card">
          <FiDollarSign />
          <div>
            <h1>Core Revenue</h1>
            <p>Elite Core transaction economics are available only to global Command Centre users.</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="core-revenue-page">
      <section className="core-revenue-hero">
        <div>
          <div className="core-revenue-eyebrow"><FiTrendingUp /> Elite Core monetization</div>
          <h1>Core Revenue</h1>
          <p>
            Observe commerce flowing through Elite Core, prove what is actually paid, model 1–3% take rates,
            and turn contracted supplier and PromoStore volume into auditable platform revenue.
          </p>
        </div>
        <div className="core-revenue-hero-actions">
          <button
            type="button"
            className="core-revenue-secondary"
            onClick={handlePaymentEvidenceSync}
            disabled={paymentSyncing || syncing}
          >
            <FiCheckCircle className={paymentSyncing ? 'is-spinning' : ''} />
            {paymentSyncing ? 'Syncing payments…' : 'Refresh QBO payments'}
          </button>
          <button type="button" className="core-revenue-secondary" onClick={() => handleSync(true)} disabled={syncing || paymentSyncing}>
            Full rescan
          </button>
          <button type="button" className="core-revenue-primary" onClick={() => handleSync(false)} disabled={syncing || paymentSyncing}>
            <FiRefreshCw className={syncing ? 'is-spinning' : ''} />
            {syncing ? 'Refreshing…' : 'Refresh ledger'}
          </button>
        </div>
      </section>

      <section className="core-revenue-posture">
        <FiShield />
        <div>
          <strong>Observation-first posture</strong>
          <span>
            The engine measures and accrues only against configured policy + verification evidence.
            It does not invoice suppliers, withhold funds, or collect fees yet.
            {summary?.counts.paymentEvidenceConflicts ? ` · ${summary.counts.paymentEvidenceConflicts} payment conflict${summary.counts.paymentEvidenceConflicts === 1 ? '' : 's'} require review.` : ''}
          </span>
        </div>
        <span className="core-revenue-posture-badge">Collection off</span>
      </section>

      {notice ? (
        <div className="core-revenue-notice">
          <span>{notice}</span>
          <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss"><FiX /></button>
        </div>
      ) : null}

      <section className="core-revenue-counts">
        <article>
          <span className="core-revenue-icon core-revenue-icon--blue"><FiDatabase /></span>
          <div><span>Observed transactions</span><strong>{summary?.counts.totalTransactions || 0}</strong></div>
        </article>
        <article>
          <span className="core-revenue-icon core-revenue-icon--green"><FiCheckCircle /></span>
          <div><span>Verified / partial</span><strong>{(summary?.counts.verified || 0) + (summary?.counts.partial || 0)}</strong></div>
        </article>
        <article>
          <span className="core-revenue-icon core-revenue-icon--purple"><FiPercent /></span>
          <div><span>Policy covered</span><strong>{summary?.counts.coveredByPolicy || 0}</strong></div>
        </article>
        <article>
          <span className="core-revenue-icon core-revenue-icon--orange"><FiClock /></span>
          <div><span>Unverified</span><strong>{summary?.counts.unverified || 0}</strong></div>
        </article>
      </section>

      {intelligence ? (
        <section className="core-revenue-ezee">
          <div className="core-revenue-ezee-head">
            <div>
              <span><FiZap /> Ezee Monetization Intelligence</span>
              <strong>{intelligence.summary}</strong>
            </div>
            <span className="core-revenue-readonly">Read only</span>
          </div>
          <div className="core-revenue-priorities">
            {intelligence.priorities.length ? intelligence.priorities.map((priority, index) => (
              <article key={`${priority.type}-${index}`}>
                <span className={`core-revenue-priority-icon priority-${priority.priority}`}>
                  {['verification-gap', 'payment-evidence-conflict'].includes(priority.type) ? <FiAlertTriangle /> : <FiTarget />}
                </span>
                <div>
                  <strong>{priority.title}</strong>
                  <p>{priority.detail}</p>
                </div>
              </article>
            )) : (
              <p className="core-revenue-muted">Ezee will surface monetization opportunities after the ledger observes commerce.</p>
            )}
          </div>
        </section>
      ) : null}

      <section className="core-revenue-section">
        <div className="core-revenue-section-head">
          <div>
            <span>Economics by currency</span>
            <h2>What a 1–3% network take rate means</h2>
          </div>
        </div>
        {loading ? (
          <div className="core-revenue-empty">Loading revenue economics…</div>
        ) : currencyBuckets.length === 0 ? (
          <div className="core-revenue-empty">
            No ledger rows yet. Run the first full rescan to observe current bills and PromoStore orders.
          </div>
        ) : (
          <div className="core-revenue-currency-grid">
            {currencyBuckets.map((bucket) => (
              <article key={bucket.currency} className="core-revenue-currency-card">
                <div className="core-revenue-currency-head">
                  <span>{bucket.currency}</span>
                  <strong>{formatAmount(bucket.observedEligible, bucket.currency)}</strong>
                  <small>observed eligible commerce</small>
                </div>
                <div className="core-revenue-currency-split">
                  <div><span>Supplier bill net</span><strong>{formatAmount(bucket.supplierBillNet, bucket.currency)}</strong></div>
                  <div><span>Verified PromoStore GMV</span><strong>{formatAmount(bucket.promoStoreVerifiedGmv, bucket.currency)}</strong></div>
                  <div><span>Verified eligible</span><strong>{formatAmount(bucket.verifiedEligible, bucket.currency)}</strong></div>
                  <div><span>Accrued now</span><strong>{formatAmount(bucket.accruedRevenue, bucket.currency)}</strong></div>
                </div>
                <div className="core-revenue-rate-grid">
                  <div><span>At 1%</span><strong>{formatAmount(bucket.simulated1Percent, bucket.currency)}</strong></div>
                  <div className="is-focus"><span>At 2%</span><strong>{formatAmount(bucket.simulated2Percent, bucket.currency)}</strong></div>
                  <div><span>At 3%</span><strong>{formatAmount(bucket.simulated3Percent, bucket.currency)}</strong></div>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="core-revenue-section">
        <div className="core-revenue-section-head">
          <div>
            <span>Partner opportunity</span>
            <h2>Top commerce counterparties</h2>
          </div>
          <button type="button" className="core-revenue-secondary" onClick={() => {
            setPolicyDraft({ ...EMPTY_POLICY });
            setShowPolicyModal(true);
          }}>
            <FiPlus /> New policy
          </button>
        </div>
        <div className="core-revenue-table-wrap">
          <table className="core-revenue-table">
            <thead>
              <tr>
                <th>Counterparty</th>
                <th>Type</th>
                <th>Observed eligible</th>
                <th>Coverage</th>
                <th>1%</th>
                <th>2%</th>
                <th>3%</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(summary?.topCounterparties || []).map((row) => (
                <tr key={`${row.counterpartyType}-${row.counterpartyId || row.counterpartyName}-${row.currency}`}>
                  <td>
                    <strong>{row.counterpartyName}</strong>
                    <small>{row.counterpartyId ? `ID ${row.counterpartyId}` : 'No canonical ID captured'}</small>
                  </td>
                  <td>{titleCase(row.counterpartyType)}</td>
                  <td><strong>{formatAmount(row.observedEligible, row.currency)}</strong></td>
                  <td>{row.coveragePercent}%</td>
                  <td>{formatAmount(row.simulated1Percent, row.currency)}</td>
                  <td><strong>{formatAmount(row.simulated2Percent, row.currency)}</strong></td>
                  <td>{formatAmount(row.simulated3Percent, row.currency)}</td>
                  <td>
                    <button type="button" className="core-revenue-table-action" onClick={() => openPolicyForCounterparty(row)}>
                      Draft 2% policy
                    </button>
                  </td>
                </tr>
              ))}
              {!summary?.topCounterparties?.length ? (
                <tr><td colSpan={8} className="core-revenue-empty">No counterparty volume observed yet.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      <section className="core-revenue-section">
        <div className="core-revenue-section-head">
          <div>
            <span>Commercial rules</span>
            <h2>Revenue policies</h2>
          </div>
          <small>{summary?.activePolicyCount || 0} active · {summary?.policyCount || 0} total</small>
        </div>
        <div className="core-revenue-policy-grid">
          {policies.map((policy) => (
            <article key={policy.id}>
              <div className="core-revenue-policy-top">
                <div>
                  <span>{titleCase(policy.sourceType)}</span>
                  <strong>{policy.name}</strong>
                </div>
                <span className={`policy-status policy-status--${policy.status}`}>{titleCase(policy.status)}</span>
              </div>
              <div className="core-revenue-policy-rate">{policy.ratePercent.toFixed(2)}%</div>
              <p>
                {policy.counterpartyType
                  ? `${titleCase(policy.counterpartyType)}${policy.counterpartyId ? ` · ${policy.counterpartyId}` : ''}`
                  : 'All matching counterparties'}
              </p>
              <div className="core-revenue-policy-flags">
                <span>{policy.includeShipping ? 'Shipping included' : 'Shipping excluded where separable'}</span>
                <span>{policy.includeTax ? 'Tax included' : 'Tax excluded'}</span>
              </div>
              <p className="core-revenue-policy-effective">
                Effective {policy.effectiveFrom ? new Date(policy.effectiveFrom).toLocaleDateString() : 'date not set'}
                {policy.effectiveTo ? ` → ${new Date(policy.effectiveTo).toLocaleDateString()}` : ''}
              </p>
              <label>
                Status
                <select value={policy.status} onChange={(event) => handlePolicyStatus(policy, event.target.value as CoreRevenuePolicy['status'])}>
                  {policyStatusOptions(policy.status).map((status) => <option key={status} value={status}>{titleCase(status)}</option>)}
                </select>
              </label>
            </article>
          ))}
          {!policies.length ? (
            <div className="core-revenue-empty core-revenue-empty--card">
              No commercial policies yet. The ledger can still model 1%, 2% and 3% against observed commerce.
            </div>
          ) : null}
        </div>
      </section>

      <section className="core-revenue-section">
        <div className="core-revenue-section-head">
          <div>
            <span>Settlement preview</span>
            <h2>
              {settlement
                ? `${new Date(settlement.dateFrom).toLocaleDateString(undefined, { month: 'long', year: 'numeric' })} Core fees`
                : 'Current month Core fees'}
            </h2>
          </div>
          <small>Read-only · no invoices or collections are created</small>
        </div>
        <div className="core-revenue-settlement-totals">
          {Object.values(settlement?.totalsByCurrency || {}).map((total) => (
            <article key={total.currency}>
              <span>{total.currency}</span>
              <strong>{formatAmount(total.netFee, total.currency)}</strong>
              <small>
                {formatAmount(total.grossFees, total.currency)} gross
                {total.adjustments > 0 ? ` · -${formatAmount(total.adjustments, total.currency)} adjustments` : ''}
              </small>
            </article>
          ))}
          {!Object.keys(settlement?.totalsByCurrency || {}).length ? (
            <div className="core-revenue-empty">No policy-backed fee movement has accrued this month yet.</div>
          ) : null}
        </div>
        {settlement?.rows?.length ? (
          <div className="core-revenue-table-wrap">
            <table className="core-revenue-table">
              <thead>
                <tr>
                  <th>Counterparty</th>
                  <th>Agreement</th>
                  <th>Verified eligible movement</th>
                  <th>Gross fees</th>
                  <th>Adjustments</th>
                  <th>Net Core fee</th>
                </tr>
              </thead>
              <tbody>
                {settlement.rows.map((row) => (
                  <tr key={`${row.policyId}-${row.counterpartyType}-${row.counterpartyId || row.counterpartyName}-${row.currency}`}>
                    <td>
                      <strong>{row.counterpartyName}</strong>
                      <small>{titleCase(row.counterpartyType)} · {row.transactionCount} transaction{row.transactionCount === 1 ? '' : 's'}</small>
                    </td>
                    <td>
                      <strong>{row.policyName}</strong>
                      <small>{row.ratePercent != null ? `${row.ratePercent.toFixed(2)}%` : 'Historical policy'}</small>
                    </td>
                    <td>{formatAmount(row.verifiedEligibleDelta, row.currency)}</td>
                    <td>{formatAmount(row.grossFees, row.currency)}</td>
                    <td>{row.adjustments > 0 ? `-${formatAmount(row.adjustments, row.currency)}` : formatAmount(0, row.currency)}</td>
                    <td><strong>{formatAmount(row.netFee, row.currency)}</strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>

      <section className="core-revenue-section">
        <div className="core-revenue-section-head">
          <div>
            <span>Financial history</span>
            <h2>Recent ledger events</h2>
          </div>
          <small>Append-only changes to verification, policy coverage and accrued fees</small>
        </div>
        <div className="core-revenue-event-list">
          {ledgerEvents.map((event) => (
            <article key={event.id}>
              <span className={`core-revenue-event-dot event-${event.eventType}`} />
              <div>
                <div className="core-revenue-event-title">
                  <strong>{titleCase(event.eventType)}</strong>
                  <span>{formatDate(event.occurredAt)}</span>
                </div>
                <p>
                  {titleCase(event.sourceType)} #{event.sourceId}
                  {event.verificationStatus ? ` · ${titleCase(event.verificationStatus)}` : ''}
                  {event.revenueStatus ? ` · ${titleCase(event.revenueStatus)}` : ''}
                </p>
              </div>
              <div className="core-revenue-event-amount">
                <strong>{formatAmount(event.feeAmount, event.currency)}</strong>
                {event.feeDelta !== 0 ? (
                  <small>{event.feeDelta > 0 ? '+' : ''}{formatAmount(event.feeDelta, event.currency)} change</small>
                ) : <small>No fee change</small>}
              </div>
            </article>
          ))}
          {!ledgerEvents.length ? (
            <div className="core-revenue-empty">Financial changes will appear here after the first ledger observation.</div>
          ) : null}
        </div>
      </section>

      <section className="core-revenue-section">
        <div className="core-revenue-section-head core-revenue-section-head--stack">
          <div>
            <span>Audit trail</span>
            <h2>Revenue ledger</h2>
            <small>{transactionTotal.toLocaleString()} tracked source transactions</small>
          </div>
          <div className="core-revenue-filters">
            <select value={sourceFilter} onChange={(event) => setSourceFilter(event.target.value)}>
              <option value="">All sources</option>
              {meta.sourceTypes.map((type) => <option key={type} value={type}>{titleCase(type)}</option>)}
            </select>
            <select value={verificationFilter} onChange={(event) => setVerificationFilter(event.target.value)}>
              <option value="">All verification</option>
              {meta.verificationStatuses.map((status) => <option key={status} value={status}>{titleCase(status)}</option>)}
            </select>
            <select value={counterpartyFilter} onChange={(event) => setCounterpartyFilter(event.target.value)}>
              <option value="">All counterparties</option>
              {meta.counterpartyTypes.map((type) => <option key={type} value={type}>{titleCase(type)}</option>)}
            </select>
          </div>
        </div>
        <div className="core-revenue-table-wrap">
          <table className="core-revenue-table core-revenue-ledger-table">
            <thead>
              <tr>
                <th>Observed</th>
                <th>Source</th>
                <th>Counterparty</th>
                <th>Net / eligible</th>
                <th>Verification</th>
                <th>Policy</th>
                <th>Core revenue</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((tx) => (
                <tr key={tx.id}>
                  <td>{formatDate(tx.observedAt)}</td>
                  <td>
                    <strong>{titleCase(tx.sourceType)}</strong>
                    <small>#{tx.sourceId}</small>
                  </td>
                  <td>
                    <strong>{tx.counterpartyName || titleCase(tx.counterpartyType)}</strong>
                    <small>{tx.instanceUniqueId || titleCase(tx.counterpartyType)}</small>
                  </td>
                  <td>
                    <strong>{formatAmount(tx.netAmount, tx.currency)}</strong>
                    <small>Eligible {formatAmount(tx.eligibleAmount, tx.currency)}</small>
                    {tx.verifiedEligibleAmount > 0 ? <small>Verified eligible {formatAmount(tx.verifiedEligibleAmount, tx.currency)}</small> : null}
                  </td>
                  <td>
                    <span className={`ledger-status ledger-status--${tx.verificationStatus}`}>{titleCase(tx.verificationStatus)}</span>
                    <small>{titleCase(tx.verificationSource)}</small>
                  </td>
                  <td>
                    {tx.policyId ? (
                      <>
                        <strong>{tx.ratePercent?.toFixed(2)}%</strong>
                        <small>{titleCase(tx.eligibilityStatus)}</small>
                      </>
                    ) : (
                      <>
                        <span>None</span>
                        <small>Observed only</small>
                      </>
                    )}
                  </td>
                  <td>
                    <strong>{formatAmount(tx.feeAmount, tx.currency)}</strong>
                    {!tx.policyId ? <small>2% model {formatAmount(tx.simulatedFee2Percent, tx.currency)}</small> : null}
                  </td>
                </tr>
              ))}
              {!transactions.length ? (
                <tr><td colSpan={7} className="core-revenue-empty">No ledger transactions match these filters.</td></tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </section>

      {showPolicyModal ? (
        <div className="core-revenue-modal-backdrop" role="presentation" onMouseDown={() => setShowPolicyModal(false)}>
          <form className="core-revenue-modal" onSubmit={handleCreatePolicy} onMouseDown={(event) => event.stopPropagation()}>
            <div className="core-revenue-modal-head">
              <div><span>Commercial agreement</span><h2>Revenue policy</h2></div>
              <button type="button" onClick={() => setShowPolicyModal(false)} aria-label="Close"><FiX /></button>
            </div>
            <p>
              A draft policy changes nothing. An active policy can accrue revenue only when its source transaction has verification evidence. Every policy is given an explicit start date so revenue is never silently applied to older commerce.
            </p>
            <label>
              Policy name
              <input required value={policyDraft.name || ''} onChange={(event) => setPolicyDraft((value) => ({ ...value, name: event.target.value }))} />
            </label>
            <div className="core-revenue-form-row">
              <label>
                Source
                <select value={policyDraft.sourceType || 'all'} onChange={(event) => setPolicyDraft((value) => ({ ...value, sourceType: event.target.value as any }))}>
                  {meta.policySourceTypes.map((type) => <option key={type} value={type}>{titleCase(type)}</option>)}
                </select>
              </label>
              <label>
                Rate %
                <input type="number" min="0" max="100" step="0.01" value={policyDraft.ratePercent ?? 2} onChange={(event) => setPolicyDraft((value) => ({ ...value, ratePercent: Number(event.target.value) }))} />
              </label>
            </div>
            <div className="core-revenue-form-row">
              <label>
                Counterparty type
                <select value={policyDraft.counterpartyType || ''} onChange={(event) => setPolicyDraft((value) => ({ ...value, counterpartyType: event.target.value ? event.target.value as any : null }))}>
                  <option value="">All counterparties</option>
                  {meta.counterpartyTypes.map((type) => <option key={type} value={type}>{titleCase(type)}</option>)}
                </select>
              </label>
              <label>
                Counterparty ID
                <input value={policyDraft.counterpartyId || ''} onChange={(event) => setPolicyDraft((value) => ({ ...value, counterpartyId: event.target.value || null }))} />
              </label>
            </div>
            <div className="core-revenue-form-row">
              <label>
                Status
                <select value={policyDraft.status || 'draft'} onChange={(event) => setPolicyDraft((value) => ({ ...value, status: event.target.value as any }))}>
                  {meta.policyStatuses.filter((status) => ['draft', 'active'].includes(status)).map((status) => <option key={status} value={status}>{titleCase(status)}</option>)}
                </select>
              </label>
              <label>
                Effective from
                <input required type="date" value={policyDraft.effectiveFrom?.slice(0, 10) || ''} onChange={(event) => setPolicyDraft((value) => ({ ...value, effectiveFrom: event.target.value || null }))} />
              </label>
            </div>
            <div className="core-revenue-policy-options">
              <label><input type="checkbox" checked={policyDraft.includeShipping === true} onChange={(event) => setPolicyDraft((value) => ({ ...value, includeShipping: event.target.checked }))} /> Include shipping</label>
              <label><input type="checkbox" checked={policyDraft.includeTax === true} onChange={(event) => setPolicyDraft((value) => ({ ...value, includeTax: event.target.checked }))} /> Include tax</label>
            </div>
            <label>
              Notes
              <textarea rows={3} value={policyDraft.notes || ''} onChange={(event) => setPolicyDraft((value) => ({ ...value, notes: event.target.value }))} />
            </label>
            <div className="core-revenue-modal-actions">
              <button type="button" className="core-revenue-secondary" onClick={() => setShowPolicyModal(false)}>Cancel</button>
              <button type="submit" className="core-revenue-primary" disabled={savingPolicy}>
                {savingPolicy ? 'Saving…' : 'Save policy'}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </div>
  );
};

export default RevenuePage;
